import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMaintenanceMode } from '../contexts/MaintenanceModeContext';
import {
  Shield,
  Settings,
  ChevronLeft,
  ToggleLeft,
  ToggleRight,
  LogOut,
  Layers,
  Package,
  Link2,
  Users,
  Sliders,
  ClipboardList,
  Key,
  BarChart3,
  Eye,
  Cpu,
  RefreshCw,
} from 'lucide-react';
import { API_ENDPOINTS, adminApiCall } from '@/config/api';

type Admin = { id: string; email: string; name: string | null; role: string };

export function AdminPanel() {
  useEffect(() => {
    document.title = 'xReplAI - Admin';
  }, []);

  const navigate = useNavigate();
  const { isMaintenanceMode, setMaintenanceMode, isLoading } = useMaintenanceMode();

  const [authLoading, setAuthLoading] = useState(true);
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loginMode, setLoginMode] = useState<'app-password' | 'email-password'>('app-password');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  const [settingsTab, setSettingsTab] = useState<'app' | 'plans' | 'packages' | 'eligibility' | 'admins' | 'ai-integration'>('app');
  const [appSettings, setAppSettings] = useState<Array<{ key: string; category: string; value: unknown; description?: string; data_type: string }>>([]);
  const [plans, setPlans] = useState<Array<Record<string, unknown>>>([]);
  const [planOptions, setPlanOptions] = useState<{
    providers: Array<{ code: string; display_name: string }>;
    models_by_provider: Record<string, Array<{ provider_model_id: string; display_name: string }>>;
  } | null>(null);
  const [packages, setPackages] = useState<Array<Record<string, unknown>>>([]);
  const [eligibility, setEligibility] = useState<Array<Record<string, unknown>>>([]);
  const [adminUsers, setAdminUsers] = useState<Array<Record<string, unknown>>>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [editPlanId, setEditPlanId] = useState<string | null>(null);
  const [editPackageId, setEditPackageId] = useState<string | null>(null);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminApiKey, setNewAdminApiKey] = useState<string | null>(null);

  const [aiIntegrationLoading, setAiIntegrationLoading] = useState(false);
  const [aiIntegration, setAiIntegration] = useState<{
    cron_sync_enabled: boolean;
    providers: Array<{
      id: string;
      code: string;
      display_name: string;
      last_synced_at: string | null;
      model_count: number;
      has_api_key: boolean;
      models: Array<{ id: string; provider_model_id: string; display_name: string | null; active: boolean }>;
    }>;
  } | null>(null);
  const [syncingProviderId, setSyncingProviderId] = useState<string | null>(null);
  const [providerIdEditingKey, setProviderIdEditingKey] = useState<string | null>(null);
  const [newApiKeyValue, setNewApiKeyValue] = useState('');
  const [savingApiKey, setSavingApiKey] = useState(false);
  const [togglingModelId, setTogglingModelId] = useState<string | null>(null);
  const [bulkTogglingProviderId, setBulkTogglingProviderId] = useState<string | null>(null);

  const [mainSection, setMainSection] = useState<'settings' | 'administration'>('settings');
  const [adminTab, setAdminTab] = useState<'users' | 'licenses' | 'usage'>('users');
  const [usageSubTab, setUsageSubTab] = useState<'today' | 'range' | 'summary'>('today');
  const [usersList, setUsersList] = useState<Array<Record<string, unknown>>>([]);
  const [licensesList, setLicensesList] = useState<Array<Record<string, unknown>>>([]);
  const [usageToday, setUsageToday] = useState<Record<string, unknown> | null>(null);
  const [usageRange, setUsageRange] = useState<Record<string, unknown> | null>(null);
  const [usageAggregate, setUsageAggregate] = useState<Record<string, unknown> | null>(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState<Record<string, unknown> | null>(null);
  const [selectedLicenseId, setSelectedLicenseId] = useState<string | null>(null);
  const [selectedLicenseDetail, setSelectedLicenseDetail] = useState<Record<string, unknown> | null>(null);
  const [licenseActionId, setLicenseActionId] = useState<string | null>(null);
  const [userActionInProgress, setUserActionInProgress] = useState(false);
  const [usageRangeFrom, setUsageRangeFrom] = useState('');
  const [usageRangeTo, setUsageRangeTo] = useState('');
  const [usageSummaryGranularity, setUsageSummaryGranularity] = useState<'hour' | 'day' | 'week' | 'month'>('day');
  const [usageSummaryFrom, setUsageSummaryFrom] = useState('');
  const [usageSummaryTo, setUsageSummaryTo] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [licensesPage, setLicensesPage] = useState(1);

  const checkAuth = useCallback(async () => {
    setAuthLoading(true);
    try {
      const data = await adminApiCall<{ admin: Admin }>(API_ENDPOINTS.ADMIN_ME);
      setAdmin(data.admin);
    } catch {
      setAdmin(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginSubmitting(true);
    try {
      if (loginMode === 'app-password') {
        const data = await adminApiCall<{ success: boolean; admin: Admin }>(API_ENDPOINTS.ADMIN_LOGIN_APP_PASSWORD, {
          method: 'POST',
          body: JSON.stringify({ password: loginPassword }),
        });
        if (data.success && data.admin) {
          setAdmin(data.admin);
          setLoginPassword('');
        }
      } else {
        const data = await adminApiCall<{ success: boolean; admin: Admin }>(API_ENDPOINTS.ADMIN_LOGIN, {
          method: 'POST',
          body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword }),
        });
        if (data.success && data.admin) {
          setAdmin(data.admin);
          setLoginEmail('');
          setLoginPassword('');
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      if (msg.includes('not configured') || msg.includes('503')) {
        setLoginError('App password not set. Use admin account (email + password) below.');
        setLoginMode('email-password');
      } else {
        setLoginError(msg);
      }
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_LOGOUT, { method: 'POST' });
    } catch {
      /* ignore */
    }
    setAdmin(null);
  };

  const toggleMode = async () => {
    setIsToggling(true);
    try {
      await setMaintenanceMode(!isMaintenanceMode);
    } catch (error) {
      console.error('Failed to toggle maintenance mode:', error);
    } finally {
      setIsToggling(false);
    }
  };

  const fetchAppSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await adminApiCall<unknown>(API_ENDPOINTS.ADMIN_SETTINGS);
      const list = Array.isArray(data) ? data : [];
      setAppSettings(list.map((s: Record<string, unknown>) => ({
        key: String(s.key),
        category: String(s.category),
        value: s.value,
        description: s.description as string | undefined,
        data_type: String(s.data_type || 'string'),
      })));
    } catch (e) {
      console.error('Failed to fetch settings', e);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const [plansData, optionsData] = await Promise.all([
        adminApiCall<{ plans: Array<Record<string, unknown>> }>(API_ENDPOINTS.ADMIN_PLANS),
        adminApiCall<{ providers: Array<{ code: string; display_name: string }>; models_by_provider: Record<string, Array<{ provider_model_id: string; display_name: string }>> }>(API_ENDPOINTS.ADMIN_PLANS_OPTIONS),
      ]);
      setPlans(plansData.plans || []);
      setPlanOptions({
        providers: optionsData.providers || [],
        models_by_provider: optionsData.models_by_provider || {},
      });
    } catch (e) {
      console.error('Failed to fetch plans', e);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await adminApiCall<{ packages: Array<Record<string, unknown>> }>(API_ENDPOINTS.ADMIN_PACKAGES);
      setPackages(data.packages || []);
    } catch (e) {
      console.error('Failed to fetch packages', e);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const fetchEligibility = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await adminApiCall<{ eligibility: Array<Record<string, unknown>> }>(API_ENDPOINTS.ADMIN_PACKAGE_ELIGIBILITY);
      setEligibility(data.eligibility || []);
    } catch (e) {
      console.error('Failed to fetch eligibility', e);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const fetchAdminUsers = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await adminApiCall<{ admin_users: Array<Record<string, unknown>> }>(API_ENDPOINTS.ADMIN_ADMIN_USERS);
      setAdminUsers(data.admin_users || []);
    } catch (e) {
      console.error('Failed to fetch admin users', e);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const fetchAiIntegration = useCallback(async () => {
    setAiIntegrationLoading(true);
    try {
      const data = await adminApiCall<{
        cron_sync_enabled: boolean;
        providers: Array<{
          id: string;
          code: string;
          display_name: string;
          last_synced_at: string | null;
          model_count: number;
          has_api_key: boolean;
          models: Array<{ id: string; provider_model_id: string; display_name: string | null; active: boolean }>;
        }>;
      }>(API_ENDPOINTS.ADMIN_AI_INTEGRATION);
      setAiIntegration({
        cron_sync_enabled: data.cron_sync_enabled ?? false,
        providers: data.providers || [],
      });
    } catch (e) {
      console.error('Failed to fetch AI integration', e);
    } finally {
      setAiIntegrationLoading(false);
    }
  }, []);

  const syncProviderModels = async (providerId: string) => {
    setSyncingProviderId(providerId);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_PROVIDER_SYNC_MODELS(providerId), { method: 'POST' });
      await fetchAiIntegration();
    } catch (e) {
      console.error('Failed to sync provider models', e);
    } finally {
      setSyncingProviderId(null);
    }
  };

  const updateCronSyncEnabled = async (enabled: boolean) => {
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_AI_INTEGRATION, {
        method: 'PATCH',
        body: JSON.stringify({ cron_sync_enabled: enabled }),
      });
      setAiIntegration((prev) => (prev ? { ...prev, cron_sync_enabled: enabled } : null));
    } catch (e) {
      console.error('Failed to update cron sync', e);
    }
  };

  const saveProviderApiKey = async (providerId: string) => {
    const key = newApiKeyValue.trim();
    if (!key) return;
    setSavingApiKey(true);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_PROVIDER_API_KEY(providerId), {
        method: 'PUT',
        body: JSON.stringify({ api_key: key }),
      });
      setProviderIdEditingKey(null);
      setNewApiKeyValue('');
      await fetchAiIntegration();
    } catch (e) {
      console.error('Failed to save API key', e);
    } finally {
      setSavingApiKey(false);
    }
  };

  const cancelEditApiKey = () => {
    setProviderIdEditingKey(null);
    setNewApiKeyValue('');
  };

  const setModelEnabled = async (modelId: string, active: boolean) => {
    setTogglingModelId(modelId);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_MODEL_ID(modelId), {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      });
      setAiIntegration((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          providers: prev.providers.map((prov) => ({
            ...prov,
            models: prov.models.map((m) => (m.id === modelId ? { ...m, active } : m)),
            model_count: prov.models.reduce((sum, m) => sum + (m.id === modelId ? (active ? 1 : 0) : m.active ? 1 : 0), 0),
          })),
        };
      });
    } catch (e) {
      console.error('Failed to update model', e);
    } finally {
      setTogglingModelId(null);
    }
  };

  const selectAllModels = async (provider: { id: string; models?: Array<{ id: string; active: boolean }> }) => {
    const toEnable = provider.models?.filter((m) => !m.active) ?? [];
    if (toEnable.length === 0) return;
    setBulkTogglingProviderId(provider.id);
    try {
      await Promise.all(
        toEnable.map((m) =>
          adminApiCall(API_ENDPOINTS.ADMIN_MODEL_ID(m.id), { method: 'PATCH', body: JSON.stringify({ active: true }) })
        )
      );
      await fetchAiIntegration();
    } catch (e) {
      console.error('Failed to select all models', e);
    } finally {
      setBulkTogglingProviderId(null);
    }
  };

  const deselectAllModels = async (provider: { id: string; models?: Array<{ id: string; active: boolean }> }) => {
    const toDisable = provider.models?.filter((m) => m.active) ?? [];
    if (toDisable.length === 0) return;
    setBulkTogglingProviderId(provider.id);
    try {
      await Promise.all(
        toDisable.map((m) =>
          adminApiCall(API_ENDPOINTS.ADMIN_MODEL_ID(m.id), { method: 'PATCH', body: JSON.stringify({ active: false }) })
        )
      );
      await fetchAiIntegration();
    } catch (e) {
      console.error('Failed to deselect all models', e);
    } finally {
      setBulkTogglingProviderId(null);
    }
  };

  useEffect(() => {
    if (!admin) return;
    if (settingsTab === 'app') fetchAppSettings();
    else if (settingsTab === 'plans') fetchPlans();
    else if (settingsTab === 'packages') fetchPackages();
    else if (settingsTab === 'eligibility') fetchEligibility();
    else if (settingsTab === 'admins') fetchAdminUsers();
    else if (settingsTab === 'ai-integration') fetchAiIntegration();
  }, [admin, settingsTab, fetchAppSettings, fetchPlans, fetchPackages, fetchEligibility, fetchAdminUsers, fetchAiIntegration]);

  const fetchUsersList = useCallback(async () => {
    setAdminLoading(true);
    try {
      const data = await adminApiCall<{ users: Array<Record<string, unknown>>; pagination: { page: number; limit: number; hasMore: boolean } }>(
        `${API_ENDPOINTS.ADMIN_USERS}?page=${usersPage}&limit=50`
      );
      setUsersList(data.users || []);
    } catch (e) {
      console.error('Failed to fetch users', e);
    } finally {
      setAdminLoading(false);
    }
  }, [usersPage]);

  const fetchLicensesList = useCallback(async () => {
    setAdminLoading(true);
    try {
      const data = await adminApiCall<{ licenses: Array<Record<string, unknown>>; pagination: { page: number; limit: number; hasMore: boolean } }>(
        `${API_ENDPOINTS.ADMIN_LICENSES}?page=${licensesPage}&limit=50`
      );
      setLicensesList(data.licenses || []);
    } catch (e) {
      console.error('Failed to fetch licenses', e);
    } finally {
      setAdminLoading(false);
    }
  }, [licensesPage]);

  const fetchUsageToday = useCallback(async () => {
    setAdminLoading(true);
    try {
      const data = await adminApiCall<Record<string, unknown>>(API_ENDPOINTS.ADMIN_USAGE_TODAY);
      setUsageToday(data);
    } catch (e) {
      console.error('Failed to fetch usage today', e);
    } finally {
      setAdminLoading(false);
    }
  }, []);

  const fetchUsageRange = useCallback(async () => {
    setAdminLoading(true);
    try {
      const params = new URLSearchParams();
      if (usageRangeFrom) params.set('from', usageRangeFrom);
      if (usageRangeTo) params.set('to', usageRangeTo);
      params.set('limit', '100');
      const data = await adminApiCall<Record<string, unknown>>(`${API_ENDPOINTS.ADMIN_USAGE_RANGE}?${params}`);
      setUsageRange(data);
    } catch (e) {
      console.error('Failed to fetch usage range', e);
    } finally {
      setAdminLoading(false);
    }
  }, [usageRangeFrom, usageRangeTo]);

  const fetchUsageAggregate = useCallback(async () => {
    setAdminLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('granularity', usageSummaryGranularity);
      if (usageSummaryFrom) params.set('from', usageSummaryFrom);
      if (usageSummaryTo) params.set('to', usageSummaryTo);
      const data = await adminApiCall<Record<string, unknown>>(`${API_ENDPOINTS.ADMIN_USAGE_AGGREGATE}?${params}`);
      setUsageAggregate(data);
    } catch (e) {
      console.error('Failed to fetch usage aggregate', e);
    } finally {
      setAdminLoading(false);
    }
  }, [usageSummaryGranularity, usageSummaryFrom, usageSummaryTo]);

  useEffect(() => {
    if (!admin || mainSection !== 'administration') return;
    if (adminTab === 'users') fetchUsersList();
    else if (adminTab === 'licenses') fetchLicensesList();
    else if (adminTab === 'usage' && usageSubTab === 'today') fetchUsageToday();
  }, [admin, mainSection, adminTab, usageSubTab, fetchUsersList, fetchLicensesList, fetchUsageToday]);

  const openUserDetail = async (userId: string) => {
    setSelectedUserId(userId);
    setAdminLoading(true);
    try {
      const data = await adminApiCall<{ user: Record<string, unknown>; licenses: Array<Record<string, unknown>> }>(API_ENDPOINTS.ADMIN_USERS_ID(userId));
      setSelectedUserDetail(data);
    } catch (e) {
      console.error('Failed to fetch user detail', e);
    } finally {
      setAdminLoading(false);
    }
  };

  const openLicenseDetail = async (licenseId: string) => {
    setSelectedLicenseId(licenseId);
    setAdminLoading(true);
    try {
      const data = await adminApiCall<{ license: Record<string, unknown> }>(API_ENDPOINTS.ADMIN_LICENSES_ID(licenseId));
      setSelectedLicenseDetail(data);
    } catch (e) {
      console.error('Failed to fetch license detail', e);
    } finally {
      setAdminLoading(false);
    }
  };

  const refetchUserDetail = async () => {
    if (!selectedUserId) return;
    try {
      const data = await adminApiCall<{ user: Record<string, unknown>; licenses: Array<Record<string, unknown>> }>(API_ENDPOINTS.ADMIN_USERS_ID(selectedUserId));
      setSelectedUserDetail(data);
    } catch (e) {
      console.error('Failed to refetch user detail', e);
    }
  };

  const deactivateUserLicense = async (licenseId: string) => {
    setLicenseActionId(licenseId);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_LICENSES_ID(licenseId), {
        method: 'PATCH',
        body: JSON.stringify({ status: 'inactive' }),
      });
      await refetchUserDetail();
    } catch (e) {
      console.error('Failed to deactivate license', e);
    } finally {
      setLicenseActionId(null);
    }
  };

  const deleteUserLicense = async (licenseId: string) => {
    if (!confirm('Permanently delete this license? This cannot be undone.')) return;
    setLicenseActionId(licenseId);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_LICENSES_ID(licenseId), { method: 'DELETE' });
      await refetchUserDetail();
    } catch (e) {
      console.error('Failed to delete license', e);
    } finally {
      setLicenseActionId(null);
    }
  };

  const deactivateLicenseInList = async (licenseId: string) => {
    setLicenseActionId(licenseId);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_LICENSES_ID(licenseId), {
        method: 'PATCH',
        body: JSON.stringify({ status: 'inactive' }),
      });
      await fetchLicensesList();
    } catch (e) {
      console.error('Failed to deactivate license', e);
    } finally {
      setLicenseActionId(null);
    }
  };

  const deleteLicenseInList = async (licenseId: string) => {
    if (!confirm('Permanently delete this license? This cannot be undone.')) return;
    setLicenseActionId(licenseId);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_LICENSES_ID(licenseId), { method: 'DELETE' });
      await fetchLicensesList();
    } catch (e) {
      console.error('Failed to delete license', e);
    } finally {
      setLicenseActionId(null);
    }
  };

  const lockUserAccount = async () => {
    if (!selectedUserId) return;
    setUserActionInProgress(true);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_USERS_ID(selectedUserId), {
        method: 'PATCH',
        body: JSON.stringify({ locked: true }),
      });
      await refetchUserDetail();
    } catch (e) {
      console.error('Failed to lock account', e);
    } finally {
      setUserActionInProgress(false);
    }
  };

  const unlockUserAccount = async () => {
    if (!selectedUserId) return;
    setUserActionInProgress(true);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_USERS_ID(selectedUserId), {
        method: 'PATCH',
        body: JSON.stringify({ locked: false }),
      });
      await refetchUserDetail();
    } catch (e) {
      console.error('Failed to unlock account', e);
    } finally {
      setUserActionInProgress(false);
    }
  };

  const deleteUserAccount = async () => {
    if (!selectedUserId) return;
    if (!confirm('Permanently delete this user and all their licenses? This cannot be undone.')) return;
    setUserActionInProgress(true);
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_USERS_ID(selectedUserId), { method: 'DELETE' });
      setSelectedUserId(null);
      setSelectedUserDetail(null);
      await fetchUsersList();
    } catch (e) {
      console.error('Failed to delete account', e);
    } finally {
      setUserActionInProgress(false);
    }
  };

  const updateSetting = async (key: string, value: unknown) => {
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_SETTINGS, {
        method: 'PUT',
        body: JSON.stringify({ key, value }),
      });
      await fetchAppSettings();
    } catch (e) {
      console.error('Failed to update setting', e);
    }
  };

  const updatePlan = async (id: string, body: Record<string, unknown>) => {
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_PLANS_ID(id), { method: 'PUT', body: JSON.stringify(body) });
      setEditPlanId(null);
      await fetchPlans();
    } catch (e) {
      console.error('Failed to update plan', e);
    }
  };

  const updatePackage = async (id: string, body: Record<string, unknown>) => {
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_PACKAGES_ID(id), { method: 'PUT', body: JSON.stringify(body) });
      setEditPackageId(null);
      await fetchPackages();
    } catch (e) {
      console.error('Failed to update package', e);
    }
  };

  const createAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAdminEmail.trim()) return;
    try {
      const data = await adminApiCall<{ api_key: string; api_key_prefix: string }>(API_ENDPOINTS.ADMIN_ADMIN_USERS, {
        method: 'POST',
        body: JSON.stringify({ email: newAdminEmail.trim(), name: newAdminName.trim() || undefined }),
      });
      setNewAdminApiKey(data.api_key || null);
      setNewAdminEmail('');
      setNewAdminName('');
      await fetchAdminUsers();
    } catch (e) {
      console.error('Failed to create admin', e);
    }
  };

  const revokeAdmin = async (email: string) => {
    if (!confirm(`Revoke admin ${email}?`)) return;
    try {
      await adminApiCall(API_ENDPOINTS.ADMIN_ADMIN_USERS_REVOKE, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      await fetchAdminUsers();
    } catch (e) {
      console.error('Failed to revoke admin', e);
    }
  };

  const cardClass = 'bg-[#0F1F3D] border border-[#1E3A8A]/30 rounded-2xl p-6';
  const inputClass = 'w-full px-4 py-3 bg-[#0A1628] border border-[#1E3A8A] rounded-lg focus:ring-2 focus:ring-[#60A5FA] outline-none text-white placeholder-gray-500';
  const btnClass = 'px-4 py-2 rounded-lg bg-gradient-to-r from-[#60A5FA] to-[#1E3A8A] text-white font-medium hover:opacity-90';
  const btnSecondary = 'px-4 py-2 rounded-lg border border-[#1E3A8A] text-[#60A5FA] hover:bg-[#1E3A8A]/20';

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!admin) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className={cardClass}>
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#60A5FA] to-[#1E3A8A] rounded-full mb-4">
                <Shield className="text-white" size={28} />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Admin Access</h1>
              <p className="text-gray-400">Sign in with your admin email and password</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              {loginMode === 'app-password' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">App password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className={inputClass}
                    placeholder="Enter app password"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Set ADMIN_APP_PASSWORD in your API environment.</p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className={inputClass}
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                    <input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className={inputClass}
                      placeholder="Password"
                      required
                    />
                  </div>
                </>
              )}
              {loginError && <p className="text-sm text-red-400">{loginError}</p>}
              <button type="submit" disabled={loginSubmitting} className={`w-full py-3 ${btnClass}`}>
                {loginSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
              {loginMode === 'email-password' && (
                <button
                  type="button"
                  onClick={() => { setLoginMode('app-password'); setLoginError(''); }}
                  className="w-full text-sm text-gray-400 hover:text-white"
                >
                  Use app password instead
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'app' as const, label: 'App Settings', icon: Sliders },
    { id: 'plans' as const, label: 'Plans', icon: Layers },
    { id: 'packages' as const, label: 'Packages', icon: Package },
    { id: 'eligibility' as const, label: 'Package Eligibility', icon: Link2 },
    { id: 'admins' as const, label: 'Admin Users', icon: Users },
    { id: 'ai-integration' as const, label: 'AI Integration', icon: Cpu },
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <button onClick={() => navigate('/')} className="text-[#60A5FA] hover:text-[#3B82F6] flex items-center gap-2 mb-2">
              <ChevronLeft size={20} />
              Back to Home
            </button>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Settings className="text-[#60A5FA]" size={32} />
              Admin Panel
            </h1>
            <p className="text-gray-400 mt-1">{admin.email}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-gray-400 hover:text-white">
            <LogOut size={18} />
            Log out
          </button>
        </div>

        <div className={cardClass + ' mb-6'}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                {isMaintenanceMode ? <ToggleRight className="text-orange-400" size={24} /> : <ToggleLeft className="text-green-400" size={24} />}
                Site Mode
              </h2>
              <p className="text-gray-400 mb-4">
                {isMaintenanceMode ? (
                  <span className="text-orange-400 font-medium">Maintenance Mode Active</span>
                ) : (
                  <span className="text-green-400 font-medium">Live Mode Active</span>
                )}
              </p>
              <p className="text-sm text-gray-500">
                {isMaintenanceMode
                  ? 'Visitors can browse the site but all CTAs will show the waitlist modal.'
                  : 'Site is fully operational with all features enabled.'}
              </p>
            </div>
            <button
              onClick={toggleMode}
              disabled={isToggling}
              className={`inline-flex h-12 w-24 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#60A5FA] focus:ring-offset-2 focus:ring-offset-[#0A1628] disabled:opacity-50 disabled:cursor-not-allowed ${
                isMaintenanceMode ? 'bg-orange-500' : 'bg-green-500'
              }`}
            >
              <span
                className={`inline-block h-10 w-10 transform rounded-full bg-white shadow-lg transition-transform ${
                  isMaintenanceMode ? 'translate-x-1' : 'translate-x-12'
                }`}
              >
                {isToggling && (
                  <svg className="animate-spin h-5 w-5 text-gray-800 mx-auto mt-2.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
              </span>
            </button>
          </div>

          {/* Status indicator */}
          <div className="mt-6 p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/50">
            <div className="flex items-start gap-3">
              <div className={`w-3 h-3 rounded-full mt-1 ${isMaintenanceMode ? 'bg-orange-400 animate-pulse' : 'bg-green-400'}`} />
              <div>
                <h3 className="text-white font-medium mb-1">
                  {isMaintenanceMode ? 'Waitlist Collection Mode' : 'Full Access Mode'}
                </h3>
                <p className="text-sm text-gray-400">
                  {isMaintenanceMode
                    ? 'Download buttons, pricing CTAs, and all action buttons will trigger the waitlist modal instead of their normal action.'
                    : 'All features are enabled and visitors can download the extension and subscribe to plans.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-[#0F1F3D] border border-[#1E3A8A]/30 rounded-xl p-4">
            <p className="text-gray-400 text-sm mb-1">Current Mode</p>
            <p className="text-2xl font-bold text-white">
              {isMaintenanceMode ? '🔧 Maintenance' : '✅ Live'}
            </p>
          </div>
          <div className="bg-[#0F1F3D] border border-[#1E3A8A]/30 rounded-xl p-4">
            <p className="text-gray-400 text-sm mb-1">CTAs Behavior</p>
            <p className="text-2xl font-bold text-white">
              {isMaintenanceMode ? '📧 Waitlist' : '⚡ Active'}
            </p>
          </div>
          <div className="bg-[#0F1F3D] border border-[#1E3A8A]/30 rounded-xl p-4">
            <p className="text-gray-400 text-sm mb-1">Status</p>
            <p className={`text-2xl font-bold ${isMaintenanceMode ? 'text-orange-400' : 'text-green-400'}`}>
              {isMaintenanceMode ? 'Pre-Launch' : 'Operational'}
            </p>
          </div>
        </div>

        {/* Main section: Settings | Administration */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMainSection('settings')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${mainSection === 'settings' ? 'bg-[#1E3A8A] text-white' : 'bg-[#0F1F3D] border border-[#1E3A8A]/30 text-gray-400 hover:text-white'}`}
          >
            <Settings size={18} />
            Settings
          </button>
          <button
            onClick={() => setMainSection('administration')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${mainSection === 'administration' ? 'bg-[#1E3A8A] text-white' : 'bg-[#0F1F3D] border border-[#1E3A8A]/30 text-gray-400 hover:text-white'}`}
          >
            <ClipboardList size={18} />
            Administration
          </button>
        </div>

        {/* Admin Instructions */}
        <div className="bg-blue-900/20 border border-blue-800 rounded-xl p-6 mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            Admin Instructions
          </h3>
          <ul className="text-sm text-gray-400 space-y-2">
            <li>• Toggle to <strong className="text-orange-400">Maintenance Mode</strong> to collect waitlist signups before launch</li>
            <li>• Toggle to <strong className="text-green-400">Live Mode</strong> when ready to allow downloads and subscriptions</li>
            <li>• Access this panel anytime at <code className="text-[#60A5FA] bg-[#0A1628] px-2 py-1 rounded">/admin</code></li>
            <li>• Changes take effect immediately across the entire site</li>
          </ul>
        </div>

        {mainSection === 'settings' && (
        <div className={cardClass}>
          <h2 className="text-xl font-semibold text-white mb-4">Settings</h2>
          <div className="flex flex-wrap gap-2 mb-6 border-b border-[#1E3A8A]/50 pb-4">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSettingsTab(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg ${settingsTab === id ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:bg-[#1E3A8A]/30 hover:text-white'}`}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>

          {settingsLoading && <p className="text-gray-400">Loading...</p>}

          {settingsTab === 'app' && !settingsLoading && (
            <div className="space-y-4">
              {appSettings.length === 0 && <p className="text-gray-400">No settings.</p>}
              {appSettings.map((s) => (
                <div key={s.key} className="flex flex-wrap items-center gap-4 p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-white font-medium">{s.key}</p>
                    {s.description && <p className="text-sm text-gray-500">{s.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {s.data_type === 'boolean' ? (
                      <button
                        onClick={() => updateSetting(s.key, !s.value)}
                        className={btnClass}
                      >
                        {String(s.value) === 'true' ? 'On' : 'Off'}
                      </button>
                    ) : (
                      <input
                        type={s.data_type === 'integer' ? 'number' : 'text'}
                        defaultValue={String(s.value ?? '')}
                        onBlur={(e) => {
                          const v = e.target.value;
                          updateSetting(s.key, s.data_type === 'integer' ? parseInt(v, 10) : v);
                        }}
                        className={inputClass + ' max-w-[200px]'}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {settingsTab === 'plans' && !settingsLoading && (
            <div className="space-y-4">
              {plans.map((plan) => (
                <div key={String(plan.id)} className="p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30">
                  {editPlanId === plan.id ? (
                    <PlanEditForm
                      plan={plan}
                      planOptions={planOptions}
                      onSave={(body) => updatePlan(plan.id as string, body)}
                      onCancel={() => setEditPlanId(null)}
                      inputClass={inputClass}
                      btnClass={btnClass}
                      btnSecondary={btnSecondary}
                    />
                  ) : (
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-white font-medium">{String(plan.display_name)} ({String(plan.name)})</p>
                        <p className="text-sm text-gray-400">${plan.price_monthly_usd}/mo · Active: {String(plan.active)}</p>
                      </div>
                      <button onClick={() => setEditPlanId(plan.id as string)} className={btnSecondary}>Edit</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {settingsTab === 'packages' && !settingsLoading && (
            <div className="space-y-4">
              {packages.map((pkg) => (
                <div key={String(pkg.id)} className="p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30">
                  {editPackageId === pkg.id ? (
                    <PackageEditForm
                      pkg={pkg}
                      onSave={(body) => updatePackage(pkg.id as string, body)}
                      onCancel={() => setEditPackageId(null)}
                      inputClass={inputClass}
                      btnClass={btnClass}
                      btnSecondary={btnSecondary}
                    />
                  ) : (
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="text-white font-medium">{String(pkg.name)}</p>
                        <p className="text-sm text-gray-400">{String(pkg.description)}</p>
                      </div>
                      <button onClick={() => setEditPackageId(pkg.id as string)} className={btnSecondary}>Edit</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {settingsTab === 'eligibility' && !settingsLoading && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                    <th className="py-2 pr-4">Package</th>
                    <th className="py-2 pr-4">Plan</th>
                    <th className="py-2 pr-4">Price USD</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibility.map((row) => {
                    const pkg = row.packages as Record<string, unknown> | undefined;
                    const plan = row.plans as Record<string, unknown> | undefined;
                    return (
                      <tr key={String(row.id)} className="border-b border-[#1E3A8A]/30">
                        <td className="py-2 pr-4 text-white">{pkg ? String(pkg.name) : '-'}</td>
                        <td className="py-2 pr-4 text-white">{plan ? String(plan.display_name ?? plan.name) : '-'}</td>
                        <td className="py-2 pr-4 text-white">${row.price_usd}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {eligibility.length === 0 && <p className="text-gray-400 py-4">No eligibility rows.</p>}
            </div>
          )}

          {settingsTab === 'ai-integration' && (
            <div className="space-y-4">
              {aiIntegrationLoading && <p className="text-gray-400">Loading...</p>}
              {!aiIntegrationLoading && aiIntegration && (
                <>
                  <div className="p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30">
                    <h3 className="text-white font-medium mb-2">Cron sync (post-MVP)</h3>
                    <p className="text-sm text-gray-400 mb-3">
                      When enabled, a cron job will sync provider models automatically. For now only manual sync is available; this switch is saved for when cron is implemented.
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateCronSyncEnabled(!aiIntegration.cron_sync_enabled)}
                        className={`inline-flex h-10 w-20 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#60A5FA] focus:ring-offset-2 focus:ring-offset-[#0A1628] ${
                          aiIntegration.cron_sync_enabled ? 'bg-[#1E3A8A]' : 'bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-8 w-8 transform rounded-full bg-white shadow transition-transform ${
                            aiIntegration.cron_sync_enabled ? 'translate-x-10' : 'translate-x-1'
                          }`}
                        />
                      </button>
                      <span className="text-gray-300">
                        {aiIntegration.cron_sync_enabled ? 'Cron sync on (post-MVP)' : 'Cron sync off (manual only)'}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-white font-medium mt-6 mb-2">Providers, API keys &amp; models</h3>
                  <p className="text-sm text-gray-400 mb-4">
                    Set each provider&apos;s API key below, then use &quot;Refresh models&quot; to sync available models from the provider.
                  </p>
                  <div className="space-y-3">
                    {aiIntegration.providers.map((p) => (
                      <div
                        key={p.id}
                        className="p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30 space-y-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <p className="text-white font-medium">{p.display_name}</p>
                            <p className="text-sm text-gray-400">
                              {p.model_count} models
                              {p.last_synced_at
                                ? ` · Last synced ${new Date(p.last_synced_at).toLocaleString()}`
                                : ' · Never synced'}
                            </p>
                          </div>
                          <button
                            onClick={() => syncProviderModels(p.id)}
                            disabled={syncingProviderId === p.id}
                            className={btnClass + ' flex items-center gap-2'}
                          >
                            {syncingProviderId === p.id ? (
                              <>
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Syncing…
                              </>
                            ) : (
                              <>
                                <RefreshCw size={16} />
                                Refresh models
                              </>
                            )}
                          </button>
                        </div>
                        <div className="border-t border-[#1E3A8A]/30 pt-3">
                          <p className="text-sm text-gray-400 mb-1">API key</p>
                          {providerIdEditingKey === p.id ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                type="password"
                                value={newApiKeyValue}
                                onChange={(e) => setNewApiKeyValue(e.target.value)}
                                placeholder="Paste API key"
                                className={inputClass + ' max-w-md'}
                                autoFocus
                              />
                              <button
                                onClick={() => saveProviderApiKey(p.id)}
                                disabled={savingApiKey || !newApiKeyValue.trim()}
                                className={btnClass}
                              >
                                {savingApiKey ? 'Saving…' : 'Save'}
                              </button>
                              <button onClick={cancelEditApiKey} className={btnSecondary}>
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">
                                {p.has_api_key ? '••••••••••••••••' : 'Not set'}
                              </span>
                              <button
                                onClick={() => { setProviderIdEditingKey(p.id); setNewApiKeyValue(''); }}
                                className={btnSecondary + ' text-sm'}
                              >
                                {p.has_api_key ? 'Update key' : 'Set key'}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="border-t border-[#1E3A8A]/30 pt-3">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                            <p className="text-sm text-gray-400">Allowed models</p>
                            {p.models && p.models.length > 0 && (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => selectAllModels(p)}
                                  disabled={bulkTogglingProviderId === p.id || p.models.every((m) => m.active)}
                                  className={btnSecondary + ' text-xs py-1 px-2'}
                                >
                                  {bulkTogglingProviderId === p.id ? 'Updating…' : 'Select all'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deselectAllModels(p)}
                                  disabled={bulkTogglingProviderId === p.id || p.models.every((m) => !m.active)}
                                  className={btnSecondary + ' text-xs py-1 px-2'}
                                >
                                  Deselect all
                                </button>
                              </div>
                            )}
                          </div>
                          {p.models && p.models.length > 0 ? (
                            <ul className="space-y-2 max-h-48 overflow-y-auto">
                              {p.models.map((m) => (
                                <li key={m.id} className="flex items-center justify-between gap-2 py-1">
                                  <span className={`text-sm ${m.active ? 'text-white' : 'text-gray-500'}`}>
                                    {m.display_name || m.provider_model_id}
                                  </span>
                                  <button
                                    onClick={() => setModelEnabled(m.id, !m.active)}
                                    disabled={togglingModelId === m.id || bulkTogglingProviderId === p.id}
                                    className={`inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#60A5FA] focus:ring-offset-1 focus:ring-offset-[#0A1628] disabled:opacity-50 ${
                                      m.active ? 'bg-[#1E3A8A]' : 'bg-gray-600'
                                    }`}
                                    title={m.active ? 'Disable model' : 'Enable model'}
                                  >
                                    <span
                                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                        m.active ? 'translate-x-5' : 'translate-x-0.5'
                                      }`}
                                    >
                                      {togglingModelId === m.id && (
                                        <svg className="animate-spin h-3 w-3 text-gray-600 mt-1 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                      )}
                                    </span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-gray-500">No models yet. Set API key and click &quot;Refresh models&quot; to sync.</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {aiIntegration.providers.length === 0 && <p className="text-gray-400">No providers. Run migration 068 to seed providers.</p>}
                  </div>
                </>
              )}
            </div>
          )}

          {settingsTab === 'admins' && !settingsLoading && (
            <div className="space-y-4">
              {newAdminApiKey && (
                <div className="p-4 bg-amber-900/30 border border-amber-600 rounded-lg">
                  <p className="text-amber-200 font-medium">Save this API key now; it will not be shown again.</p>
                  <code className="block mt-2 p-2 bg-[#0A1628] rounded text-sm break-all text-white">{newAdminApiKey}</code>
                  <button onClick={() => setNewAdminApiKey(null)} className={btnSecondary + ' mt-2'}>Dismiss</button>
                </div>
              )}
              <form onSubmit={createAdmin} className="flex flex-wrap gap-4 items-end p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email</label>
                  <input value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} className={inputClass + ' w-48'} placeholder="new@example.com" required />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Name</label>
                  <input value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} className={inputClass + ' w-40'} placeholder="Optional" />
                </div>
                <button type="submit" className={btnClass}>Create admin</button>
              </form>
              <div className="space-y-2">
                {adminUsers.map((u) => (
                  <div key={String(u.id)} className="flex items-center justify-between flex-wrap gap-2 p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30">
                    <div>
                      <p className="text-white font-medium">{String(u.email)}</p>
                      <p className="text-sm text-gray-400">{String(u.name || '-')} · {String(u.role)} · prefix {String(u.api_key_prefix || '-')}</p>
                    </div>
                    {!(u.revoked_at) && u.email !== admin.email && (
                      <button onClick={() => revokeAdmin(String(u.email))} className="text-red-400 hover:text-red-300 text-sm">Revoke</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {mainSection === 'administration' && (
        <div className={cardClass}>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <ClipboardList size={22} />
            Administration
          </h2>
          <div className="flex flex-wrap gap-2 mb-6 border-b border-[#1E3A8A]/50 pb-4">
            <button
              onClick={() => setAdminTab('users')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${adminTab === 'users' ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:bg-[#1E3A8A]/30 hover:text-white'}`}
            >
              <Users size={18} />
              Users
            </button>
            <button
              onClick={() => setAdminTab('licenses')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${adminTab === 'licenses' ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:bg-[#1E3A8A]/30 hover:text-white'}`}
            >
              <Key size={18} />
              Licenses
            </button>
            <button
              onClick={() => setAdminTab('usage')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${adminTab === 'usage' ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:bg-[#1E3A8A]/30 hover:text-white'}`}
            >
              <BarChart3 size={18} />
              Usage
            </button>
          </div>

          {adminLoading && <p className="text-gray-400 mb-4">Loading...</p>}

          {adminTab === 'users' && !adminLoading && (
            <div className="space-y-4">
              {(selectedUserDetail && selectedUserId) ? (
                <div>
                  <button onClick={() => { setSelectedUserId(null); setSelectedUserDetail(null); }} className="text-[#60A5FA] hover:underline mb-4 flex items-center gap-1">
                    <ChevronLeft size={18} /> Back to list
                  </button>
                  <div className="p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30 mb-4">
                    <p className="text-white font-medium">{String(selectedUserDetail.user?.email)}</p>
                    <p className="text-sm text-gray-400">{String(selectedUserDetail.user?.name ?? '-')} · {String(selectedUserDetail.user?.created_at)}</p>
                    <p className="text-sm mt-1">
                      <span className={selectedUserDetail.user?.locked ? 'text-amber-400' : 'text-gray-400'}>
                        Account: {selectedUserDetail.user?.locked ? 'Locked' : 'Active'}
                      </span>
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {selectedUserDetail.user?.locked ? (
                        <button
                          type="button"
                          onClick={unlockUserAccount}
                          disabled={userActionInProgress}
                          className="text-amber-400 hover:text-amber-300 text-sm disabled:opacity-50"
                        >
                          {userActionInProgress ? '…' : 'Unlock Account'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={lockUserAccount}
                          disabled={userActionInProgress}
                          className="text-amber-400 hover:text-amber-300 text-sm disabled:opacity-50"
                        >
                          {userActionInProgress ? '…' : 'Lock Account'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={deleteUserAccount}
                        disabled={userActionInProgress}
                        className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50"
                      >
                        {userActionInProgress ? '…' : 'Delete Account'}
                      </button>
                    </div>
                  </div>
                  <h3 className="text-white font-medium mb-2">Licenses</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                          <th className="py-2 pr-4">License #</th>
                          <th className="py-2 pr-4">Plan</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Billing</th>
                          <th className="py-2 pr-4">Used / Limit</th>
                          <th className="py-2 pr-4">Overage</th>
                          <th className="py-2 pr-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(selectedUserDetail.licenses) ? selectedUserDetail.licenses : []).map((lic: Record<string, unknown>) => {
                          const plan = lic.plan as Record<string, unknown> | undefined;
                          const maxGen = plan?.max_generations_per_month;
                          const licId = String(lic.id);
                          const acting = licenseActionId === licId;
                          const isActive = String(lic.status) === 'active';
                          return (
                            <tr key={licId} className="border-b border-[#1E3A8A]/30">
                              <td className="py-2 pr-4 text-white font-mono text-sm">{String(lic.license_key ?? lic.id).slice(0, 12)}…</td>
                              <td className="py-2 pr-4 text-white">{plan ? String(plan.display_name ?? plan.name) : '-'}</td>
                              <td className="py-2 pr-4 text-white">{String(lic.status)}</td>
                              <td className="py-2 pr-4 text-gray-400">{String(lic.billing_cycle ?? '-')}</td>
                              <td className="py-2 pr-4 text-white">{Number(lic.generations_used ?? 0)} / {maxGen == null ? '∞' : maxGen}</td>
                              <td className="py-2 pr-4 text-white">{Number(lic.overage_used ?? 0)}</td>
                              <td className="py-2 pr-4">
                                <div className="flex items-center gap-2">
                                  {isActive && (
                                    <button
                                      type="button"
                                      onClick={() => deactivateUserLicense(licId)}
                                      disabled={acting}
                                      className="text-amber-400 hover:text-amber-300 text-sm disabled:opacity-50"
                                    >
                                      {acting ? '…' : 'Deactivate'}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => deleteUserLicense(licId)}
                                    disabled={acting}
                                    className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50"
                                  >
                                    {acting ? '…' : 'Delete'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {(!selectedUserDetail.licenses || (selectedUserDetail.licenses as unknown[]).length === 0) && (
                      <p className="text-gray-400 py-4">No licenses.</p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                          <th className="py-2 pr-4">Email</th>
                          <th className="py-2 pr-4">Name</th>
                          <th className="py-2 pr-4">Created</th>
                          <th className="py-2 pr-4"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersList.map((u) => (
                          <tr key={String(u.id)} className="border-b border-[#1E3A8A]/30">
                            <td className="py-2 pr-4 text-white">{String(u.email)}</td>
                            <td className="py-2 pr-4 text-gray-400">{String(u.name ?? '-')}</td>
                            <td className="py-2 pr-4 text-gray-400">{String(u.created_at ?? '-')}</td>
                            <td className="py-2 pr-4">
                              <button onClick={() => openUserDetail(u.id as string)} className="flex items-center gap-1 text-[#60A5FA] hover:underline text-sm">
                                <Eye size={14} /> View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {usersList.length === 0 && <p className="text-gray-400 py-4">No users.</p>}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setUsersPage((p) => Math.max(1, p - 1))} disabled={usersPage <= 1} className={btnSecondary}>Previous</button>
                    <span className="text-gray-400 py-2">Page {usersPage}</span>
                    <button onClick={() => setUsersPage((p) => p + 1)} className={btnSecondary}>Next</button>
                  </div>
                </>
              )}
            </div>
          )}

          {adminTab === 'licenses' && !adminLoading && (
            <div className="space-y-4">
              {(selectedLicenseDetail && selectedLicenseId) ? (
                <div>
                  <button onClick={() => { setSelectedLicenseId(null); setSelectedLicenseDetail(null); }} className="text-[#60A5FA] hover:underline mb-4 flex items-center gap-1">
                    <ChevronLeft size={18} /> Back to list
                  </button>
                  {(() => {
                    const lic = (selectedLicenseDetail as { license?: Record<string, unknown> }).license;
                    if (!lic) return null;
                    const user = lic.user as Record<string, unknown> | undefined;
                    const plan = lic.plan as Record<string, unknown> | undefined;
                    const usage = lic.usage_current_month as Record<string, unknown> | undefined;
                    return (
                      <div className="p-4 bg-[#0A1628] rounded-lg border border-[#1E3A8A]/30 space-y-2">
                        <p className="text-white font-medium">License {String(lic.license_key).slice(0, 16)}…</p>
                        <p className="text-sm text-gray-400">User: {user ? String(user.email) : '-'}</p>
                        <p className="text-sm text-gray-400">Plan: {plan ? String(plan.display_name ?? plan.name) : '-'}</p>
                        <p className="text-sm text-gray-400">Status: {String(lic.status)} · Billing: {String(lic.billing_cycle)}</p>
                        <p className="text-sm text-gray-400">Current month: {Number(usage?.generations_used ?? 0)} in-plan, {Number(usage?.overage_used ?? 0)} overage · Total events: {Number(lic.usage_tracking_total_events ?? 0)}</p>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                          <th className="py-2 pr-4">License</th>
                          <th className="py-2 pr-4">User</th>
                          <th className="py-2 pr-4">Plan</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 pr-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {licensesList.map((lic) => {
                          const licId = String(lic.id);
                          const acting = licenseActionId === licId;
                          const isActive = String(lic.status) === 'active';
                          return (
                            <tr key={licId} className="border-b border-[#1E3A8A]/30">
                              <td className="py-2 pr-4 text-white font-mono text-sm">{String(lic.license_key ?? lic.id).slice(0, 14)}…</td>
                              <td className="py-2 pr-4 text-gray-400">{String(lic.user_email ?? '-')}</td>
                              <td className="py-2 pr-4 text-white">{String(lic.plan_name ?? '-')}</td>
                              <td className="py-2 pr-4 text-gray-400">{String(lic.status)}</td>
                              <td className="py-2 pr-4">
                                <div className="flex flex-wrap items-center gap-2">
                                  <button onClick={() => openLicenseDetail(licId)} className="flex items-center gap-1 text-[#60A5FA] hover:underline text-sm">
                                    <Eye size={14} /> View
                                  </button>
                                  {isActive && (
                                    <button
                                      type="button"
                                      onClick={() => deactivateLicenseInList(licId)}
                                      disabled={acting}
                                      className="text-amber-400 hover:text-amber-300 text-sm disabled:opacity-50"
                                    >
                                      {acting ? '…' : 'Deactivate'}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => deleteLicenseInList(licId)}
                                    disabled={acting}
                                    className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50"
                                  >
                                    {acting ? '…' : 'Delete'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {licensesList.length === 0 && <p className="text-gray-400 py-4">No licenses.</p>}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => setLicensesPage((p) => Math.max(1, p - 1))} disabled={licensesPage <= 1} className={btnSecondary}>Previous</button>
                    <span className="text-gray-400 py-2">Page {licensesPage}</span>
                    <button onClick={() => setLicensesPage((p) => p + 1)} className={btnSecondary}>Next</button>
                  </div>
                </>
              )}
            </div>
          )}

          {adminTab === 'usage' && !adminLoading && (
            <div className="space-y-4">
              <div className="flex gap-2 border-b border-[#1E3A8A]/30 pb-2 mb-4">
                <button onClick={() => setUsageSubTab('today')} className={`px-3 py-1 rounded ${usageSubTab === 'today' ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:text-white'}`}>Today</button>
                <button onClick={() => setUsageSubTab('range')} className={`px-3 py-1 rounded ${usageSubTab === 'range' ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:text-white'}`}>Range</button>
                <button onClick={() => setUsageSubTab('summary')} className={`px-3 py-1 rounded ${usageSubTab === 'summary' ? 'bg-[#1E3A8A] text-white' : 'text-gray-400 hover:text-white'}`}>Summary</button>
              </div>

              {usageSubTab === 'today' && usageToday && (
                <div>
                  <p className="text-gray-400 mb-2">From {String(usageToday.from)}</p>
                  <p className="text-white font-medium">Total events: {Number(usageToday.total_events ?? 0)} · In-plan: {Number(usageToday.total_in_plan ?? 0)} · Overage: {Number(usageToday.total_overage ?? 0)}</p>
                  <div className="mt-4 overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                          <th className="py-1 pr-2">Time</th>
                          <th className="py-1 pr-2">License</th>
                          <th className="py-1 pr-2">Model</th>
                          <th className="py-1 pr-2">In-plan</th>
                          <th className="py-1 pr-2">Overage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(Array.isArray(usageToday.events) ? (usageToday.events as Array<Record<string, unknown>>).slice(0, 50) : []).map((e, i) => (
                          <tr key={e.id ?? i} className="border-b border-[#1E3A8A]/30">
                            <td className="py-1 pr-2 text-gray-400">{String(e.created_at).slice(11, 19)}</td>
                            <td className="py-1 pr-2 font-mono text-xs">{String(e.license_id).slice(0, 8)}…</td>
                            <td className="py-1 pr-2 text-gray-400">{String(e.model_used ?? '-')}</td>
                            <td className="py-1 pr-2">{Number(e.generations_in_plan ?? 0)}</td>
                            <td className="py-1 pr-2">{e.overage_generation ? 1 : 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {usageSubTab === 'range' && (
                <div>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">From (ISO)</label>
                      <input value={usageRangeFrom} onChange={(e) => setUsageRangeFrom(e.target.value)} className={inputClass + ' w-48'} placeholder="2025-01-01T00:00:00Z" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">To (ISO)</label>
                      <input value={usageRangeTo} onChange={(e) => setUsageRangeTo(e.target.value)} className={inputClass + ' w-48'} placeholder="2025-01-31T23:59:59Z" />
                    </div>
                    <button onClick={fetchUsageRange} className={btnClass + ' self-end'}>Fetch</button>
                  </div>
                  {usageRange && (
                    <>
                      <p className="text-white font-medium">Total events: {Number(usageRange.total_events ?? 0)} · In-plan: {Number(usageRange.total_in_plan ?? 0)} · Overage: {Number(usageRange.total_overage ?? 0)}</p>
                      <div className="mt-4 overflow-x-auto max-h-64 overflow-y-auto">
                        <table className="w-full text-left text-sm">
                          <thead>
                            <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                              <th className="py-1 pr-2">Time</th>
                              <th className="py-1 pr-2">License</th>
                              <th className="py-1 pr-2">Model</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(usageRange.events) ? (usageRange.events as Array<Record<string, unknown>>).slice(0, 50) : []).map((e, i) => (
                              <tr key={e.id ?? i} className="border-b border-[#1E3A8A]/30">
                                <td className="py-1 pr-2 text-gray-400">{String(e.created_at)}</td>
                                <td className="py-1 pr-2 font-mono text-xs">{String(e.license_id).slice(0, 8)}…</td>
                                <td className="py-1 pr-2 text-gray-400">{String(e.model_used ?? '-')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {usageSubTab === 'summary' && (
                <div>
                  <div className="flex flex-wrap gap-4 mb-4">
                    <select value={usageSummaryGranularity} onChange={(e) => setUsageSummaryGranularity(e.target.value as 'hour' | 'day' | 'week' | 'month')} className={inputClass + ' w-32'}>
                      <option value="hour">Hour</option>
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                    </select>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">From (optional)</label>
                      <input value={usageSummaryFrom} onChange={(e) => setUsageSummaryFrom(e.target.value)} className={inputClass + ' w-48'} placeholder="2025-01-01" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">To (optional)</label>
                      <input value={usageSummaryTo} onChange={(e) => setUsageSummaryTo(e.target.value)} className={inputClass + ' w-48'} placeholder="2025-01-31" />
                    </div>
                    <button onClick={fetchUsageAggregate} className={btnClass + ' self-end'}>Fetch</button>
                  </div>
                  {usageAggregate && Array.isArray(usageAggregate.aggregated) && (
                    <div className="overflow-x-auto max-h-80 overflow-y-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="text-gray-400 border-b border-[#1E3A8A]/50">
                            <th className="py-1 pr-2">Period</th>
                            <th className="py-1 pr-2">Count</th>
                            <th className="py-1 pr-2">In-plan</th>
                            <th className="py-1 pr-2">Overage</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(usageAggregate.aggregated as Array<Record<string, unknown>>).map((row, i) => (
                            <tr key={row.period ?? i} className="border-b border-[#1E3A8A]/30">
                              <td className="py-1 pr-2 text-white">{String(row.period)}</td>
                              <td className="py-1 pr-2">{Number(row.count ?? 0)}</td>
                              <td className="py-1 pr-2">{Number(row.in_plan ?? 0)}</td>
                              <td className="py-1 pr-2">{Number(row.overage ?? 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function PlanEditForm({
  plan,
  planOptions,
  onSave,
  onCancel,
  inputClass,
  btnClass,
  btnSecondary,
}: {
  plan: Record<string, unknown>;
  planOptions: { providers: Array<{ code: string; display_name: string }>; models_by_provider: Record<string, Array<{ provider_model_id: string; display_name: string }>> } | null;
  onSave: (body: Record<string, unknown>) => void;
  onCancel: () => void;
  inputClass: string;
  btnClass: string;
  btnSecondary: string;
}) {
  const [displayName, setDisplayName] = useState(String(plan.display_name ?? ''));
  const [description, setDescription] = useState(String(plan.description ?? ''));
  const [priceMonthly, setPriceMonthly] = useState(plan.price_monthly_usd != null ? Number(plan.price_monthly_usd) : 0);
  const [priceYearly, setPriceYearly] = useState(plan.price_yearly_usd != null ? Number(plan.price_yearly_usd) : 0);
  const [stripePriceIdMonthly, setStripePriceIdMonthly] = useState(String(plan.stripe_price_id_monthly ?? ''));
  const [stripePriceIdYearly, setStripePriceIdYearly] = useState(String(plan.stripe_price_id_yearly ?? ''));
  const [maxVariants, setMaxVariants] = useState(Number(plan.max_variants) ?? 1);
  const [maxGoals, setMaxGoals] = useState(Number(plan.max_goals) ?? 1);
  const [maxTones, setMaxTones] = useState(Number(plan.max_tones) ?? 1);
  const [maxDevices, setMaxDevices] = useState(Number(plan.max_devices) ?? 1);
  const [maxGenerationsPerMonth, setMaxGenerationsPerMonth] = useState(
    plan.max_generations_per_month == null ? '' : String(plan.max_generations_per_month)
  );
  const [includedPackagesCount, setIncludedPackagesCount] = useState<'0' | '1' | 'all'>(() => {
    const v = plan.included_packages_count;
    if (v == null) return 'all';
    if (v === 1) return '1';
    return '0';
  });
  const [canPurchasePackages, setCanPurchasePackages] = useState(Boolean(plan.can_purchase_packages));
  const [allowedProviders, setAllowedProviders] = useState(
    Array.isArray(plan.allowed_providers) ? (plan.allowed_providers as string[]).join(', ') : String(plan.allowed_providers ?? 'grok')
  );
  const [defaultProvider, setDefaultProvider] = useState(String(plan.default_provider ?? 'grok'));
  const [defaultModel, setDefaultModel] = useState(String(plan.default_model ?? ''));
  const [allowedModelsJson, setAllowedModelsJson] = useState(
    typeof plan.allowed_models === 'object' && plan.allowed_models != null
      ? JSON.stringify(plan.allowed_models, null, 2)
      : String(plan.allowed_models ?? '{}')
  );
  const [selectedProviderCodes, setSelectedProviderCodes] = useState<string[]>(() =>
    Array.isArray(plan.allowed_providers) ? (plan.allowed_providers as string[]).filter(Boolean) : ['grok']
  );
  const [allowedModelIdsByProvider, setAllowedModelIdsByProvider] = useState<Record<string, string[]>>(() => {
    const am = plan.allowed_models;
    if (typeof am === 'object' && am != null && !Array.isArray(am)) return am as Record<string, string[]>;
    return {};
  });
  const [featuresJson, setFeaturesJson] = useState(
    typeof plan.features === 'object' && plan.features != null
      ? JSON.stringify(plan.features, null, 2)
      : String(plan.features ?? '{}')
  );
  const [analysisProvider, setAnalysisProvider] = useState(String(plan.analysis_provider ?? ''));
  const [analysisModel, setAnalysisModel] = useState(String(plan.analysis_model ?? ''));
  const [active, setActive] = useState(Boolean(plan.active));
  const [displayOrder, setDisplayOrder] = useState(Number(plan.display_order) ?? 0);
  const [jsonError, setJsonError] = useState('');

  const handleSave = () => {
    let features: Record<string, unknown> = {};
    try {
      features = featuresJson.trim() ? JSON.parse(featuresJson) : {};
    } catch {
      setJsonError('features: invalid JSON');
      return;
    }
    let allowedModels: Record<string, string[]> | null = null;
    const useStructured = planOptions && planOptions.providers.length > 0;
    if (!useStructured) {
      try {
        allowedModels = allowedModelsJson.trim() ? JSON.parse(allowedModelsJson) : null;
      } catch {
        setJsonError('allowed_models: invalid JSON');
        return;
      }
    }
    setJsonError('');
    onSave({
      display_name: displayName,
      description: description || null,
      price_monthly_usd: priceMonthly,
      price_yearly_usd: priceYearly,
      stripe_price_id_monthly: stripePriceIdMonthly.trim() || null,
      stripe_price_id_yearly: stripePriceIdYearly.trim() || null,
      max_variants: maxVariants,
      max_goals: maxGoals,
      max_tones: maxTones,
      max_devices: maxDevices,
      max_generations_per_month: maxGenerationsPerMonth === '' ? null : Number(maxGenerationsPerMonth),
      included_packages_count: includedPackagesCount === 'all' ? null : Number(includedPackagesCount),
      can_purchase_packages: canPurchasePackages,
      allowed_providers: useStructured ? selectedProviderCodes : allowedProviders.split(',').map((s) => s.trim()).filter(Boolean),
      default_provider: useStructured ? (selectedProviderCodes.includes(defaultProvider) ? defaultProvider : selectedProviderCodes[0] || null) : (defaultProvider.trim() || null),
      default_model: useStructured
        ? ((allowedModelIdsByProvider[defaultProvider] || []).includes(defaultModel) ? defaultModel : (allowedModelIdsByProvider[defaultProvider]?.[0] ?? null))
        : (defaultModel.trim() || null),
      allowed_models: useStructured ? allowedModelIdsByProvider : allowedModels,
      analysis_provider: analysisProvider.trim() || null,
      analysis_model: analysisModel.trim() || null,
      features,
      active,
      display_order: displayOrder,
    });
  };

  const textareaClass = inputClass + ' min-h-[80px] font-mono text-sm';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Display name</label>
          <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Display order</label>
          <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(Number(e.target.value))} className={inputClass} />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={textareaClass} rows={2} />
      </div>

      <div className="border-t border-[#1E3A8A]/30 pt-4">
        <p className="text-sm font-medium text-gray-300 mb-2">Pricing</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Price monthly USD</label>
            <input type="number" step="0.01" value={priceMonthly} onChange={(e) => setPriceMonthly(Number(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Price yearly USD</label>
            <input type="number" step="0.01" value={priceYearly} onChange={(e) => setPriceYearly(Number(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Stripe Price ID (monthly)</label>
            <input value={stripePriceIdMonthly} onChange={(e) => setStripePriceIdMonthly(e.target.value)} className={inputClass} placeholder="price_..." />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Stripe Price ID (yearly)</label>
            <input value={stripePriceIdYearly} onChange={(e) => setStripePriceIdYearly(e.target.value)} className={inputClass} placeholder="price_..." />
          </div>
        </div>
      </div>

      <div className="border-t border-[#1E3A8A]/30 pt-4">
        <p className="text-sm font-medium text-gray-300 mb-2">Limits</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max variants</label>
            <input type="number" min={0} value={maxVariants} onChange={(e) => setMaxVariants(Number(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max goals</label>
            <input type="number" min={0} value={maxGoals} onChange={(e) => setMaxGoals(Number(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max tones</label>
            <input type="number" min={0} value={maxTones} onChange={(e) => setMaxTones(Number(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max devices</label>
            <input type="number" min={0} value={maxDevices} onChange={(e) => setMaxDevices(Number(e.target.value))} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max generations/month</label>
            <input type="number" min={0} value={maxGenerationsPerMonth} onChange={(e) => setMaxGenerationsPerMonth(e.target.value)} className={inputClass} placeholder="empty = unlimited" />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Included packages count</label>
            <select
              value={includedPackagesCount}
              onChange={(e) => setIncludedPackagesCount((e.target.value as '0' | '1' | 'all'))}
              className={inputClass}
            >
              <option value="0">None (0)</option>
              <option value="1">One (1)</option>
              <option value="all">All (unlimited)</option>
            </select>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <input type="checkbox" id="canPurchase" checked={canPurchasePackages} onChange={(e) => setCanPurchasePackages(e.target.checked)} />
          <label htmlFor="canPurchase" className="text-gray-300 text-sm">Can purchase packages</label>
        </div>
      </div>

      <div className="border-t border-[#1E3A8A]/30 pt-4">
        <p className="text-sm font-medium text-gray-300 mb-2">Providers &amp; models</p>
        {planOptions && planOptions.providers.length > 0 ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Allowed providers</label>
              <div className="flex flex-wrap gap-3">
                {planOptions.providers.map((prov) => {
                  const checked = selectedProviderCodes.includes(prov.code);
                  return (
                    <label key={prov.code} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          if (checked) {
                            setSelectedProviderCodes((prev) => prev.filter((c) => c !== prov.code));
                            setAllowedModelIdsByProvider((prev) => { const next = { ...prev }; delete next[prov.code]; return next; });
                            if (defaultProvider === prov.code) setDefaultProvider(selectedProviderCodes[0] || '');
                          } else {
                            setSelectedProviderCodes((prev) => [...prev, prov.code].sort());
                          }
                        }}
                        className="rounded border-[#1E3A8A]"
                      />
                      <span className="text-gray-300">{prov.display_name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Default provider</label>
              <select
                value={selectedProviderCodes.includes(defaultProvider) ? defaultProvider : (selectedProviderCodes[0] ?? '')}
                onChange={(e) => { setDefaultProvider(e.target.value); setDefaultModel(''); }}
                className={inputClass}
              >
                {selectedProviderCodes.map((code) => {
                  const prov = planOptions.providers.find((p) => p.code === code);
                  return <option key={code} value={code}>{prov?.display_name ?? code}</option>;
                })}
                {selectedProviderCodes.length === 0 && <option value="">— Select providers above —</option>}
              </select>
            </div>
            {selectedProviderCodes.map((code) => {
              const models = planOptions.models_by_provider[code] || [];
              const selectedIds = allowedModelIdsByProvider[code] || [];
              const prov = planOptions.providers.find((p) => p.code === code);
              if (models.length === 0) {
                return (
                  <div key={code} className="p-2 bg-[#0A1628] rounded border border-[#1E3A8A]/30">
                    <p className="text-sm text-gray-400">{prov?.display_name ?? code}: no enabled models. Enable models in AI Integration.</p>
                  </div>
                );
              }
              return (
                <div key={code} className="p-3 bg-[#0A1628] rounded border border-[#1E3A8A]/30">
                  <p className="text-sm font-medium text-gray-300 mb-2">Allowed models for {prov?.display_name ?? code}</p>
                  <div className="flex flex-wrap gap-3">
                    {models.map((m) => {
                      const checked = selectedIds.includes(m.provider_model_id);
                      return (
                        <label key={m.provider_model_id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setAllowedModelIdsByProvider((prev) => {
                                const arr = prev[code] || [];
                                const next = { ...prev };
                                if (checked) {
                                  next[code] = arr.filter((id) => id !== m.provider_model_id);
                                } else {
                                  next[code] = [...arr, m.provider_model_id].sort();
                                }
                                return next;
                              });
                              if (defaultProvider === code && defaultModel === m.provider_model_id && checked) setDefaultModel('');
                            }}
                            className="rounded border-[#1E3A8A]"
                          />
                          <span className="text-gray-300">{m.display_name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Default model</label>
              <select
                value={(allowedModelIdsByProvider[defaultProvider] || []).includes(defaultModel) ? defaultModel : ''}
                onChange={(e) => setDefaultModel(e.target.value)}
                className={inputClass}
              >
                <option value="">— None —</option>
                {(allowedModelIdsByProvider[defaultProvider] || []).map((modelId) => {
                  const models = planOptions.models_by_provider[defaultProvider] || [];
                  const m = models.find((x) => x.provider_model_id === modelId);
                  return <option key={modelId} value={modelId}>{m?.display_name ?? modelId}</option>;
                })}
              </select>
              <p className="text-xs text-gray-500 mt-1">Must be one of the allowed models for the default provider.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Allowed providers (comma-separated)</label>
              <input value={allowedProviders} onChange={(e) => setAllowedProviders(e.target.value)} className={inputClass} placeholder="grok, openai" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Default provider</label>
              <input value={defaultProvider} onChange={(e) => setDefaultProvider(e.target.value)} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-400 mb-1">Default model</label>
              <input value={defaultModel} onChange={(e) => setDefaultModel(e.target.value)} className={inputClass} placeholder="grok-4-latest" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm text-gray-400 mb-1">Allowed models (JSON)</label>
              <textarea value={allowedModelsJson} onChange={(e) => setAllowedModelsJson(e.target.value)} className={textareaClass} rows={4} placeholder='{"grok": ["grok-4-latest"], "openai": ["gpt-4o"]}' />
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-[#1E3A8A]/30 pt-4">
        <p className="text-sm font-medium text-gray-300 mb-2">Analysis (classification, style, goals, tones)</p>
        <p className="text-sm text-gray-400 mb-3">Provider and model used by analysis endpoints. Drafting continues to use the user&apos;s chosen provider/model in the extension.</p>
        {planOptions && planOptions.providers.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Analysis provider</label>
              <select
                value={planOptions.providers.some((p) => p.code === analysisProvider) ? analysisProvider : ''}
                onChange={(e) => {
                  const code = e.target.value;
                  setAnalysisProvider(code);
                  const models = planOptions.models_by_provider[code] || [];
                  const stillValid = models.some((m) => m.provider_model_id === analysisModel);
                  if (!stillValid) setAnalysisModel(models[0]?.provider_model_id ?? '');
                }}
                className={inputClass}
              >
                <option value="">— None (use default) —</option>
                {planOptions.providers.map((p) => (
                  <option key={p.code} value={p.code}>{p.display_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Analysis model</label>
              <select
                value={analysisProvider ? ((planOptions.models_by_provider[analysisProvider] || []).some((m) => m.provider_model_id === analysisModel) ? analysisModel : '') : ''}
                onChange={(e) => setAnalysisModel(e.target.value)}
                className={inputClass}
                disabled={!analysisProvider}
              >
                <option value="">— None (use provider default) —</option>
                {(planOptions.models_by_provider[analysisProvider] || []).map((m) => (
                  <option key={m.provider_model_id} value={m.provider_model_id}>{m.display_name ?? m.provider_model_id}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Load plan options to select analysis provider/model.</p>
        )}
      </div>

      <div className="border-t border-[#1E3A8A]/30 pt-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">Features (JSON)</label>
        <textarea value={featuresJson} onChange={(e) => setFeaturesJson(e.target.value)} className={textareaClass} rows={4} placeholder='{"style_mimicking": true, "advanced_classification": true}' />
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <input type="checkbox" id="active" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <label htmlFor="active" className="text-gray-300">Active</label>
        </div>
        {jsonError && <span className="text-sm text-red-400">{jsonError}</span>}
      </div>

      <div className="flex gap-2">
        <button onClick={handleSave} className={btnClass}>Save</button>
        <button onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}

function PackageEditForm({
  pkg,
  onSave,
  onCancel,
  inputClass,
  btnClass,
  btnSecondary,
}: {
  pkg: Record<string, unknown>;
  onSave: (body: Record<string, unknown>) => void;
  onCancel: () => void;
  inputClass: string;
  btnClass: string;
  btnSecondary: string;
}) {
  const [name, setName] = useState(String(pkg.name ?? ''));
  const [description, setDescription] = useState(String(pkg.description ?? ''));

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Description</label>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} />
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave({ name, description })} className={btnClass}>Save</button>
        <button onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </div>
  );
}
